import { useEffect, useState } from 'react';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { checkPortalRole, clearPortalRoleCache, markPortalDenied } from '../services/portalRole';
import { clearMfaDeferral, deferMfaEnrollment, isMfaDeferred } from '../services/mfaDeferral';
import MfaEnrollmentGate from './MfaEnrollmentGate';
import MfaReminderBanner from './MfaReminderBanner';

// Opt-in staff-MFA requirement. OFF unless the env explicitly sets "1" —
// the Clerk instance must have TOTP/backup codes enabled (Dashboard →
// Configure → Multi-factor) before this flag is turned on, or the gate's
// enrollment UI has nothing to offer.
const REQUIRE_STAFF_MFA = import.meta.env.VITE_REQUIRE_STAFF_MFA === '1';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Client-side route guard for the School-Admin SPA.
 *
 * SECURITY MODEL — this guard is a UX layer only. The real authorization
 * boundary is on the server:
 *   1. Clerk issues session JWTs that include a `role` claim sourced from
 *      `public_metadata.role` (configured in Clerk Dashboard → Sessions →
 *      Customize session token).
 *   2. Every school-admin API endpoint runs through `SchoolAdminAuthGuard`
 *      (Hypebook-API/src/auth/guards/school-admin-auth.guard.ts), which
 *      verifies the JWT signature against Clerk's JWKS and checks
 *      `UserRole.SCHOOL_ADMIN` on the user record.
 *   3. This guard runs the shared wrong-portal check (portalRole.ts) so a
 *      non-school-admin with a valid session — e.g. a student deep-linking
 *      to /dashboard — is signed out with an explanation instead of landing
 *      on a shell whose every request 403s. SignIn.tsx runs the same check
 *      right after auth completes; the shared cache means whichever runs
 *      first pays the single profile fetch.
 *
 * Bypassing this React guard (e.g., disabling JS) only exposes the empty
 * admin UI shell — every data fetch still requires a valid Clerk JWT with
 * the SCHOOL_ADMIN role, which an attacker cannot mint.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const { isSignedIn, isLoaded, getToken, userId } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const location = useLocation();
  // 'checking' → hold rendering; 'ok' → render; denial signs out (redirect).
  const [roleState, setRoleState] = useState<'checking' | 'ok'>('checking');
  // "Remind me later" on the MFA gate. Seeded from storage so a deferral
  // survives reloads; setting it re-renders straight past the gate.
  const [mfaDeferred, setMfaDeferred] = useState(() => isMfaDeferred(userId));

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) return;
    let cancelled = false;
    void (async () => {
      const verdict = await checkPortalRole(userId, getToken);
      if (cancelled) return;
      if (verdict === 'denied') {
        // Re-check on the next sign-in rather than replaying a stale denial
        // (their role may have been fixed in the meantime). The denied flag
        // rides sessionStorage because Clerk's dev-instance signOut redirect
        // drops the ?denied=1 query.
        clearPortalRoleCache();
        markPortalDenied();
        await signOut({ redirectUrl: '/signin?denied=1' });
        return;
      }
      // 'ok' or 'unknown' — render; the API guards remain the real boundary.
      setRoleState('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, userId, getToken, signOut]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('auth.validatingSession')}</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (roleState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('auth.validatingSession')}</p>
        </div>
      </div>
    );
  }

  // Staff MFA requirement (opt-in via VITE_REQUIRE_STAFF_MFA). Runs after
  // the wrong-portal check, so only confirmed staff ever see the gate.
  // `user` can be null for a beat after isSignedIn — only gate on a loaded
  // user with a definite "no second factor", never on missing data.
  const needsMfa = REQUIRE_STAFF_MFA && !!user && !user.twoFactorEnabled;

  if (needsMfa && !mfaDeferred) {
    return (
      <MfaEnrollmentGate
        onDefer={() => {
          deferMfaEnrollment(userId ?? '');
          setMfaDeferred(true);
        }}
      />
    );
  }

  // Enrolled while deferred — drop the stored deferral so a future lapse
  // (e.g. they remove their second factor) starts a fresh grace period
  // rather than silently reusing the old one.
  if (mfaDeferred && user && user.twoFactorEnabled) {
    clearMfaDeferral(userId);
  }

  return (
    <>
      {children}
      {/* Nag until they actually enrol; the deferral expires on its own and
          the blocking gate returns. */}
      {needsMfa && mfaDeferred ? <MfaReminderBanner onSetUp={() => setMfaDeferred(false)} /> : null}
    </>
  );
};
